import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import { DISLIKE_REASONS, type DislikeReasonCode } from "../types/chatFeedback";
import styles from "./AdminChatFeedback.module.css";

type ReviewStatus = "pending" | "reviewing" | "resolved" | "dismissed";
type LifecycleStatus = "active" | "withdrawn";
type Priority = "low" | "normal" | "high";

interface FeedbackListItem {
  id: string;
  reason_codes: DislikeReasonCode[];
  reporter_email?: string | null;
  reporter_name?: string | null;
  question_preview: string;
  lifecycle_status: LifecycleStatus;
  review_status: ReviewStatus;
  priority: Priority;
  notification_status: string;
  created_at: string;
}

interface FeedbackMessage {
  id: string;
  sent_to_email: string;
  subject: string;
  body: string;
  delivery_status: "sending" | "sent" | "failed";
  delivery_error?: string | null;
  created_at: string;
}

interface FeedbackDetail extends Omit<FeedbackListItem, "question_preview"> {
  user_id: string;
  thread_id: string;
  message_id: string;
  question_snapshot: Record<string, any>;
  answer_snapshot: Record<string, any>;
  model_metadata: Record<string, any>;
  admin_notes?: string | null;
  notified_at?: string | null;
  withdrawn_at?: string | null;
  notification_last_error?: string | null;
  messages: FeedbackMessage[];
}

const reasonLabels = new Map(
  DISLIKE_REASONS.map((reason) => [reason.code, reason.label])
);

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleString() : "—";

const formatAge = (value: string) => {
  const elapsedSeconds = Math.max(
    0,
    Math.floor((Date.now() - new Date(value).getTime()) / 1000)
  );
  if (elapsedSeconds < 60) return "just now";
  const minutes = Math.floor(elapsedSeconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days < 30 ? `${days}d ago` : formatDate(value);
};

const snapshotText = (snapshot: Record<string, any> | undefined) => {
  if (!snapshot) return "";
  const value = snapshot.text ?? snapshot.answer ?? snapshot.fallback_text ?? snapshot.content;
  return typeof value === "string" ? value : value ? JSON.stringify(value, null, 2) : "";
};

const scalarText = (value: unknown, fallback = "") =>
  typeof value === "string" || typeof value === "number"
    ? String(value)
    : fallback;

const AdminChatFeedback: React.FC = () => {
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("feedback_id");

  const [items, setItems] = useState<FeedbackListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [reviewStatus, setReviewStatus] = useState("open");
  const [lifecycleStatus, setLifecycleStatus] = useState("active");
  const [priority, setPriority] = useState("");
  const [reasonCode, setReasonCode] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");

  const [detail, setDetail] = useState<FeedbackDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [replySubject, setReplySubject] = useState(
    "Update on your C-Shaped chat feedback"
  );
  const [replyBody, setReplyBody] = useState("");
  const [replySending, setReplySending] = useState(false);
  const [replyStatus, setReplyStatus] = useState("");

  const limit = 30;

  const listQuery = useMemo(() => {
    const params = new URLSearchParams({
      review_status: reviewStatus,
      lifecycle_status: lifecycleStatus,
      limit: String(limit),
      offset: String(offset),
    });
    if (priority) params.set("priority", priority);
    if (reasonCode) params.set("reason_code", reasonCode);
    if (dateFrom) params.set("date_from", `${dateFrom}T00:00:00`);
    if (dateTo) params.set("date_to", `${dateTo}T23:59:59`);
    return params.toString();
  }, [reviewStatus, lifecycleStatus, priority, reasonCode, dateFrom, dateTo, offset]);

  const loadList = useCallback(async () => {
    setListLoading(true);
    setListError("");
    try {
      const response = await authenticatedFetch(
        `${apiUrl}/api/admin/chat-feedback?${listQuery}`
      );
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Could not load feedback");
      setItems(payload.items || []);
      setTotal(payload.total || 0);
    } catch (error) {
      setListError(error instanceof Error ? error.message : "Could not load feedback");
    } finally {
      setListLoading(false);
    }
  }, [apiUrl, listQuery]);

  const loadDetail = useCallback(
    async (feedbackId: string) => {
      setDetailLoading(true);
      setDetailError("");
      try {
        const response = await authenticatedFetch(
          `${apiUrl}/api/admin/chat-feedback/${feedbackId}`
        );
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.error || "Could not load case");
        setDetail(payload);
        setAdminNotes(payload.admin_notes || "");
      } catch (error) {
        setDetail(null);
        setDetailError(error instanceof Error ? error.message : "Could not load case");
      } finally {
        setDetailLoading(false);
      }
    },
    [apiUrl]
  );

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    if (selectedId) void loadDetail(selectedId);
    else setDetail(null);
  }, [selectedId, loadDetail]);

  const selectCase = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("feedback_id", id);
    else next.delete("feedback_id");
    setSearchParams(next);
  };

  const updateCase = async (updates: Record<string, unknown>) => {
    if (!detail) return;
    setSaving(true);
    setDetailError("");
    try {
      const response = await authenticatedFetch(
        `${apiUrl}/api/admin/chat-feedback/${detail.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updates),
        }
      );
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Could not update case");
      setDetail(payload);
      setAdminNotes(payload.admin_notes || "");
      await loadList();
    } catch (error) {
      setDetailError(error instanceof Error ? error.message : "Could not update case");
    } finally {
      setSaving(false);
    }
  };

  const sendReply = async () => {
    if (!detail || !replySubject.trim() || !replyBody.trim()) return;
    setReplySending(true);
    setReplyStatus("");
    try {
      const response = await authenticatedFetch(
        `${apiUrl}/api/admin/chat-feedback/${detail.id}/reply`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            subject: replySubject.trim(),
            body: replyBody.trim(),
          }),
        }
      );
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Could not send email");
      setDetail(payload.feedback);
      setReplyBody("");
      setReplyStatus("Email sent and recorded.");
    } catch (error) {
      setReplyStatus(error instanceof Error ? error.message : "Could not send email");
      await loadDetail(detail.id);
    } finally {
      setReplySending(false);
    }
  };

  const references: any[] = detail && Array.isArray(detail.answer_snapshot?.references)
    ? detail.answer_snapshot.references
    : [];

  return (
    <div className={styles.page}>
      <div className={styles.heading}>
        <div>
          <h1>Chat Feedback</h1>
          <p>Review disliked agent answers and contact the reporting user.</p>
        </div>
        <span className={styles.totalBadge}>{total} cases</span>
      </div>

      <div className={styles.filters}>
        <label>
          Review status
          <select value={reviewStatus} onChange={(event) => { setOffset(0); setReviewStatus(event.target.value); }}>
            <option value="open">Open</option>
            <option value="pending">Pending</option>
            <option value="reviewing">Reviewing</option>
            <option value="resolved">Resolved</option>
            <option value="dismissed">Dismissed</option>
            <option value="all">All</option>
          </select>
        </label>
        <label>
          Lifecycle
          <select value={lifecycleStatus} onChange={(event) => { setOffset(0); setLifecycleStatus(event.target.value); }}>
            <option value="active">Active</option>
            <option value="withdrawn">Withdrawn</option>
            <option value="all">All</option>
          </select>
        </label>
        <label>
          Priority
          <select value={priority} onChange={(event) => { setOffset(0); setPriority(event.target.value); }}>
            <option value="">Any</option>
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
          </select>
        </label>
        <label>
          Reason
          <select value={reasonCode} onChange={(event) => { setOffset(0); setReasonCode(event.target.value); }}>
            <option value="">Any</option>
            {DISLIKE_REASONS.map((reason) => (
              <option key={reason.code} value={reason.code}>{reason.label}</option>
            ))}
          </select>
        </label>
        <label>
          From
          <input type="date" value={dateFrom} onChange={(event) => { setOffset(0); setDateFrom(event.target.value); }} />
        </label>
        <label>
          To
          <input type="date" value={dateTo} onChange={(event) => { setOffset(0); setDateTo(event.target.value); }} />
        </label>
      </div>

      {listError && <div className={styles.errorBanner} role="alert">{listError}</div>}

      <div className={styles.workspace}>
        <section className={styles.listPanel} aria-label="Feedback cases">
          {listLoading ? (
            <div className={styles.emptyState}>Loading feedback…</div>
          ) : items.length === 0 ? (
            <div className={styles.emptyState}>No disliked answers match these filters.</div>
          ) : (
            items.map((item) => (
              <button
                type="button"
                key={item.id}
                className={`${styles.caseRow} ${selectedId === item.id ? styles.caseRowSelected : ""}`}
                onClick={() => selectCase(item.id)}
              >
                <div className={styles.caseRowTop}>
                  <span className={`${styles.statusPill} ${styles[`status_${item.review_status}`]}`}>{item.review_status}</span>
                  <span className={styles.priority}>{item.priority}</span>
                  <time
                    dateTime={item.created_at}
                    title={formatDate(item.created_at)}
                  >
                    {formatAge(item.created_at)}
                  </time>
                </div>
                <strong>{item.question_preview || "Question unavailable"}</strong>
                <div className={styles.reasonList}>
                  {item.reason_codes.length > 0
                    ? item.reason_codes.map((reason) => <span key={reason}>{reasonLabels.get(reason) || reason}</span>)
                    : <span>No reason selected</span>}
                </div>
                <small>{item.reporter_name || item.reporter_email || "Unknown reporter"} · email {item.notification_status}</small>
              </button>
            ))
          )}
          <div className={styles.pagination}>
            <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>Previous</button>
            <span>{total === 0 ? 0 : offset + 1}–{Math.min(offset + limit, total)} of {total}</span>
            <button type="button" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>Next</button>
          </div>
        </section>

        <section className={styles.detailPanel} aria-label="Feedback case details">
          {!selectedId ? (
            <div className={styles.emptyState}>Select a feedback case to review its answer.</div>
          ) : detailLoading ? (
            <div className={styles.emptyState}>Loading case…</div>
          ) : detailError && !detail ? (
            <div className={styles.errorBanner} role="alert">{detailError}</div>
          ) : detail ? (
            <>
              <div className={styles.detailHeader}>
                <div>
                  <button type="button" className={styles.closeDetail} onClick={() => selectCase(null)}>← Back to list</button>
                  <h2>Feedback case</h2>
                  <p>{detail.reporter_name || "Unknown name"} · {detail.reporter_email || "Unknown email"}</p>
                </div>
                <div className={styles.caseIdentifiers}>Case {detail.id}<br />Message {detail.message_id}</div>
              </div>

              {detailError && <div className={styles.errorBanner} role="alert">{detailError}</div>}

              <div className={styles.workflowControls}>
                <label>Review status
                  <select disabled={saving} value={detail.review_status} onChange={(event) => void updateCase({ review_status: event.target.value })}>
                    <option value="pending">Pending</option>
                    <option value="reviewing">Reviewing</option>
                    <option value="resolved">Resolved</option>
                    <option value="dismissed">Dismissed</option>
                  </select>
                </label>
                <label>Priority
                  <select disabled={saving} value={detail.priority} onChange={(event) => void updateCase({ priority: event.target.value })}>
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                  </select>
                </label>
                <div className={styles.lifecycleSummary}>
                  <strong>{detail.lifecycle_status}</strong>
                  <span>Notification {detail.notification_status}</span>
                </div>
              </div>

              <div className={styles.reasonListLarge}>
                {detail.reason_codes.length > 0
                  ? detail.reason_codes.map((reason) => <span key={reason}>{reasonLabels.get(reason) || reason}</span>)
                  : <span>No reason selected</span>}
              </div>

              <article className={styles.snapshotCard}>
                <h3>User question</h3>
                <div className={styles.snapshotText}>{snapshotText(detail.question_snapshot) || "Question unavailable"}</div>
              </article>

              <article className={styles.snapshotCard}>
                <h3>Agent answer</h3>
                <div className={styles.snapshotText}>{detail.answer_snapshot?.answer || snapshotText(detail.answer_snapshot) || "Answer unavailable"}</div>
                {detail.answer_snapshot?.justification && <><h4>Justification</h4><div className={styles.snapshotText}>{detail.answer_snapshot.justification}</div></>}
                <h4>References</h4>
                {references.length === 0 ? <p className={styles.muted}>No references included.</p> : references.map((reference: any, index: number) => {
                  const source = scalarText(
                    reference.source || reference.textbook || reference.source_name || reference.reference,
                    "Source"
                  );
                  const edition = scalarText(reference.edition || reference.details?.edition);
                  const page = scalarText(reference.page_number || reference.pageNumber || reference.details?.page_number);
                  const excerpt = scalarText(reference.text_excerpt || reference.citedText);
                  return (
                    <div className={styles.reference} key={`${index}-${source}`}>
                      <strong>{index + 1}. {source}</strong>
                      {(edition || page) && <small>{edition ? `Edition ${edition}` : ""}{page ? `${edition ? " · " : ""}p. ${page}` : ""}</small>}
                      {excerpt && <p>{excerpt}</p>}
                    </div>
                  );
                })}
              </article>

              <article className={styles.metadataCard}>
                <h3>Metadata</h3>
                <dl>
                  <div><dt>Reported</dt><dd>{formatDate(detail.created_at)}</dd></div>
                  <div><dt>Notified</dt><dd>{formatDate(detail.notified_at)}</dd></div>
                  <div><dt>Thread</dt><dd>{detail.thread_id}</dd></div>
                  {Object.entries(detail.model_metadata || {}).map(([key, value]) => (
                    <div key={key}><dt>{key.replace(/_/g, " ")}</dt><dd>{typeof value === "object" ? JSON.stringify(value) : String(value)}</dd></div>
                  ))}
                </dl>
              </article>

              <article className={styles.editorCard}>
                <h3>Internal notes</h3>
                <textarea value={adminNotes} onChange={(event) => setAdminNotes(event.target.value)} rows={4} placeholder="Notes visible only to admins" />
                <button type="button" disabled={saving} onClick={() => void updateCase({ admin_notes: adminNotes })}>{saving ? "Saving…" : "Save notes"}</button>
              </article>

              <article className={styles.editorCard}>
                <h3>Email the user</h3>
                <p className={styles.muted}>Outbound email is recorded here. Replies go to the configured admin inbox.</p>
                <label>Subject<input value={replySubject} maxLength={200} onChange={(event) => setReplySubject(event.target.value)} /></label>
                <label>Message<textarea value={replyBody} maxLength={10000} rows={6} onChange={(event) => setReplyBody(event.target.value)} /></label>
                <button type="button" disabled={replySending || !replySubject.trim() || !replyBody.trim()} onClick={() => void sendReply()}>{replySending ? "Sending…" : "Send email"}</button>
                {replyStatus && <div className={styles.replyStatus} role="status">{replyStatus}</div>}
              </article>

              <article className={styles.messageHistory}>
                <h3>Reply history</h3>
                {detail.messages.length === 0 ? <p className={styles.muted}>No admin emails sent yet.</p> : detail.messages.map((message) => (
                  <div key={message.id} className={styles.sentMessage}>
                    <div><strong>{message.subject}</strong><span>{message.delivery_status} · {formatDate(message.created_at)}</span></div>
                    <p>{message.body}</p>
                    <small>To {message.sent_to_email}{message.delivery_error ? ` · ${message.delivery_error}` : ""}</small>
                  </div>
                ))}
              </article>
            </>
          ) : null}
        </section>
      </div>
    </div>
  );
};

export default AdminChatFeedback;
